import React, { useEffect, useState, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./manageSubscription.css";
import ConfirmModal from "../../confirmModal/ConfirmModal";
import axios from "axios";
import { Context } from "../../../context/Context";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

// Stripe plan intervals returned from `plan.interval` (e.g., "month", "year")
const PLAN_INTERVALS = {
  MONTH: "month",
  YEAR: "year",
};

const PLAN_LABELS = {
  monthly: "Monthly Plan",
  annually: "Annual Plan",
  [PLAN_INTERVALS.MONTH]: "Monthly Plan",  // Stripe interval
  [PLAN_INTERVALS.YEAR]: "Annual Plan",    // Stripe interval
};

const getPlanLabel = (key) => PLAN_LABELS[key?.toLowerCase()] || "—";

const PLAN_PRICING = {
  "monthly": "$10/month",
  "annually": "$90/year",
};

const LEGACY_PLACEHOLDER_ID = "123"; // TEMP: used for legacy accounts with no real subscription
const LEGACY_PLACEHOLDER_PLAN = "plan"; // TEMP: used for legacy accounts with no real subscription

const normalizePlan = (plan) => {
  const cleaned = plan?.trim().toLowerCase();
  if (["monthly", "month"].includes(cleaned)) return "monthly";
  if (["annually", "annual", "year"].includes(cleaned)) return "annually";
  if (cleaned === LEGACY_PLACEHOLDER_PLAN) return LEGACY_PLACEHOLDER_PLAN;
  return null;
};

const ManageSubscription = ({ onSubscriptionCancelled }) => {
  const { user, token } = useContext(Context);
  const [subscription, setSubscription] = useState({});
  const [subscriptionDetails, setSubscriptionDetails] = useState({});
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const normalizedPlan = useMemo(() => normalizePlan(subscription.plan),[subscription.plan]);

  const refreshSubscription = async () => {
    try {
      const res = await axios.get(`/users/${user.userId}?t=${Date.now()}`);
      const planType = normalizePlan(res.data.subscription.plan);
      const subscriptionId = res.data.subscription.subscriptionId;
      setSubscription({ plan: planType, subscriptionId });

      await getSubscriptionDetails(); // This uses updated subscriptionId
    } catch (err) {
      console.error("Failed to refresh subscription:", err);
      toast.error("Could not refresh subscription.");
    }
  };

  useEffect(() => {
    const getSubscription = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await axios.get(`/users/${user.userId}?t=${Date.now()}`);
        if (res.status !== 200) {
          toast.info(res.data.msg);
        }

        const planType = normalizePlan(res.data.subscription.plan);
        const subscriptionId = res.data.subscription.subscriptionId;
        setSubscription({ plan: planType, subscriptionId: subscriptionId });

      } catch (err) {
        console.error("Error getting Subscription! " + err);
        setError("Failed to fetch subscription details.");
        toast.error("Error Fetching Subscription Details!");
      } finally {
        setLoading(false);
      }
    };

    // Always fetch on mount + on Stripe checkout return
    const query = new URLSearchParams(location.search);
    const isCheckoutReturn = query.get("checkout") === "complete";
    getSubscription();
    if (isCheckoutReturn) {
      navigate(location.pathname, { replace: true }); // Clean up URL
    }
  }, [user.userId, location.search, navigate, location.pathname]);

  const isSubscribed = useMemo(() => {
    return (
      subscription?.subscriptionId?.trim() &&
      subscription.subscriptionId !== LEGACY_PLACEHOLDER_ID &&
      normalizedPlan !== LEGACY_PLACEHOLDER_PLAN &&
      ["monthly", "annually"].includes(normalizedPlan)
    );
  }, [subscription]);

  const getSubscriptionDetails = async () => {
    setLoading(true);
    setError(null);

    if (!subscription?.subscriptionId?.trim() || subscription.subscriptionId === LEGACY_PLACEHOLDER_ID) {
      setSubscriptionDetails({});
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(
        `/stripe/get-subscription/${subscription.subscriptionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.status !== 200 && res.data?.msg) {
        toast.info(res.data.msg);
      }

      if (!res.data?.subscription) {
        throw new Error("Subscription data is missing from response.");
      }

      const {
        currentPeriodStart,
        currentPeriodEnd,
        paymentMethod,
        planInterval: rawPlanInterval,
        cancelAtPeriodEnd,
        status,
      } = res.data.subscription;

      const planInterval = rawPlanInterval?.toLowerCase() || null;

      setSubscriptionDetails({
        startDate: currentPeriodStart,
        endDate: currentPeriodEnd,
        paymentMethod,
        planInterval,
        cancelAtPeriodEnd,
        status,
      });

    } catch (err) {
      console.error("Error getting Subscription Details: ", err);

      // Handle 404 from backend (already-canceled/deleted Stripe sub)
      if (err.response?.status === 404) {
        setSubscriptionDetails({});
        setSubscription({ plan: null, subscriptionId: null });
        setError("Your previous subscription was removed. Please select a new plan to continue.");
      } else {
        setError("Failed to retrieve billing information.");
      }

      if (err.response?.status !== 404) {
        toast.error("Error Fetching Subscription Details!");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSubscriptionDetails();
  }, [subscription.subscriptionId]);

  const handleSubscribe = async (newPlan) => {
    setError(null);

    setLoading(true);

    try {
      const userId = user?._id;

      if (subscription.subscriptionId) {
        const res = await axios.post(
          "/stripe/update-subscription",
          {
            subscriptionId: subscription.subscriptionId,
            plan: newPlan,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.status !== 200) {
          toast.info(res.data.msg);
        } else {
          toast.success("Subscription updated successfully!");
          await refreshSubscription();
        }
      } else {
        const res = await axios.post(
          "/stripe/create-checkout-session",
          {
            plan: newPlan,
            userId,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.status !== 200 || !res.data?.url) {
          console.error("Unexpected Stripe response:", res.data);
          toast.error("Stripe is currently unavailable. Please try again in a few minutes.");
        } else {
          window.location.href = res.data.url;
        }
      }
    } catch (error) {
      console.error("Checkout session creation failed:", error);
      const message =
        error?.response?.data?.error ||
        "Stripe checkout failed. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setLoading(true);
    setError(null);

    setShowCancelModal(false);
    try {
      const res = await axios.post(
        "/stripe/cancel-subscription",
        {
          subscriptionId: subscription.subscriptionId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.status === 200) {
        toast.success("Subscription cancelled.");
        await refreshSubscription();
        onSubscriptionCancelled(false);
      } else {
        toast.error("Failed to cancel subscription.");
      }
    } catch (error) {
      console.error("Error cancelling subscription: " + error);
      setError("Cancellation failed. Please try again.");
      toast.error("Failed to cancel subscription.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleResume = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(
        "/stripe/resume-subscription",
        {
          subscriptionId: subscription.subscriptionId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.status === 200) {
        toast.success("Subscription resumed.");

        await getSubscriptionDetails();

      } else {
        toast.error("Failed to resume subscription.");
      }
    } catch (err) {
      console.error("Resume error:", err);
      toast.error(err.response?.data?.error || "Resume failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="manage-subscription">
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <p style={{ fontSize: "16px", color: "gray" }} aria-live="polite">
              Loading your subscription info...
            </p>
            <div className="spinner" role="status"></div>
          </div>
        )}
        {error && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: "red", fontSize: "30px", marginBottom: "5px" }} />
            <p style={{ fontSize: "16px", color: "red" }} aria-live="polite">
              {error}
            </p>
          </div>
        )}
        <h1 className="heading-primary">

          {isSubscribed
            ? `Current Plan: ${getPlanLabel(normalizedPlan)} (${PLAN_PRICING[normalizedPlan]})`
            : "You are not yet subscribed."}
        </h1>

        {isSubscribed && (
          <>
            <h2 className="heading-secondary">
              Subscription Start: {formatDate(subscriptionDetails?.startDate)}
            </h2>
            <h2 className="heading-secondary">
              Next Renewal: {formatDate(subscriptionDetails?.endDate)}
            </h2>
          </>
        )}
        {!isSubscribed && (
          <>
            <div className="not-subscribed-container">
              {subscriptionDetails?.planInterval ? (
                <p className="not-subscribed-message">
                  You previously had a subscription. Select a plan below to renew!
                </p>
              ) : (
                <p className="not-subscribed-message">
                  Select a plan below to access our full library of articles!
                </p>
              )}
            </div>
          </>
        )}
      </div>

      <div className="horizontal_line" style={{ marginBottom: "10px" }}></div>

      <div className="manage-subscription">
        <h1 className="heading-primary">
          {isSubscribed ? "Change Your Plan" : "Choose the plan that's right for you!"}
        </h1>

        <div className="subscription-cards">
          <div className="subscription-card monthly">
            <h2 style={{ marginTop: "25px", marginLeft: "25px" }}>
              MONTHLY PLAN
            </h2>
            <p
              style={{
                marginTop: "15px",
                marginLeft: "25px",
                fontSize: "18px",
              }}
            >
              Full access with flexibility — Upgrade to annual anytime.
            </p>
            <p
              style={{
                fontSize: "24px",
                fontWeight: "400",
                marginTop: "15px",
                marginLeft: "25px",
              }}
            >
              $10 <span style={{ fontSize: "18px", fontWeight: "400" }}>/Month</span>
            </p>
            <p
              style={{
                fontSize: "18px",
                fontWeight: "400",
                marginTop: "15px",
                marginLeft: "25px",
              }}
            >
              Only $2.50 per week
            </p>
            {normalizedPlan === "monthly" ? (
              <div className="badge-wrapper">
                <span className="plan-badge-monthly">✓ Current Plan</span>
              </div>
            ) : (
              <>
              <button
                disabled={loading}
                type="button"
                className="subscribe-change-plan"
                style={{
                  marginTop: "30px",
                  backgroundColor: "#272635",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  height: "36px",
                  width: "160px",
                  fontSize: "16px",
                  marginLeft: "auto",
                  marginRight: "auto",
                  display: "block"
                }}
                onClick={() => {
                  if (!isSubscribed) {
                    handleSubscribe("monthly");
                  } else {
                    setShowChangeModal("monthly");
                  }
                }}
              >
                <span aria-live="polite">
                  {loading
                    ? "Please wait..."
                    : isSubscribed
                    ? "Switch to Monthly"
                    : "Choose Monthly"}
                </span>
              </button>
              {isSubscribed && showChangeModal === "monthly" && (
                <ConfirmModal
                  message="You're currently on our best value plan. Are you sure you want to switch to monthly billing?"
                  onConfirm={() => handleSubscribe("monthly")}
                  onCancel={() => setShowChangeModal(false)}
                />
              )}
              </>
            )}
          </div>

          <div className="subscription-card annually">
            <div
              style={{
                position: "absolute",
                top: "-20px",
                right: "-20px",
                width: "180px",
                height: "40px",
                backgroundColor: "#FAFAFA",
                color: "#272635",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "700",
                borderRadius: "10px",
                fontSize: "18px",
                boxShadow: "0 6px 12px rgba(0, 0, 0, 0.25), 0 4px 4px rgba(0, 0, 0, 0.12)",
              }}
            >
              3 MONTHS FREE!
            </div>
            <h2 style={{ marginTop: "25px", marginLeft: "25px" }}>
              ANNUAL PLAN
            </h2>
            <p
              style={{
                marginTop: "15px",
                marginLeft: "25px",
                fontSize: "18px",
              }}
            >
              Best value — Pay once a year and enjoy full access all year long!
            </p>
            <p
              style={{
                fontSize: "24px",
                fontWeight: "400",
                marginTop: "15px",
                marginLeft: "25px",
              }}
            >
              $90 <span style={{ fontSize: "18px", fontWeight: "400" }}>/Year</span>
            </p>
            <p
              style={{
                fontSize: "18px",
                fontWeight: "400",
                marginTop: "15px",
                marginLeft: "25px",
              }}
            >
              Only $1.75 per week
            </p>
            {normalizedPlan === "annually" ? (
              <div className="badge-wrapper">
                <span className="plan-badge-annual">✓ Current Plan</span>
              </div>
            ) : (
              <>
              <button
                disabled={loading}
                type="button"
                className="subscribe-change-plan"
                style={{
                  marginTop: "30px",
                  backgroundColor: "#272635",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  height: "36px",
                  width: "160px",
                  fontSize: "16px",
                  marginLeft: "auto",
                  marginRight: "auto",
                  display: "block"
                }}
                onClick={() => {
                  if (!isSubscribed) {
                    handleSubscribe("annually");
                  } else {
                    setShowChangeModal("annually");
                  }
                }}
              >
                <span aria-live="polite">
                  {loading
                    ? "Please wait..."
                    : isSubscribed
                    ? "Switch to Annual"
                    : "Choose Annual"}
                </span>
              </button>
              {isSubscribed && showChangeModal === "annually" && (
                <ConfirmModal
                  message="Switching to annual gives you our best value. Pay once and enjoy full access all year long. Ready to switch?"
                  onConfirm={() => handleSubscribe("annually")}
                  onCancel={() => setShowChangeModal(false)}
                />
              )}
              </>
            )}
          </div>
        </div>


      </div>

      <div className="horizontal_line"></div>

      {isSubscribed && (
        <>
          <div className="manage-subscription">
            <h1 style={{ fontSize: "24px", fontWeight: "700", marginTop: "10px" }}>
              Payment Methods:
            </h1>
            {subscriptionDetails.paymentMethod ? (
              <>
                <h3>
                  {subscriptionDetails.paymentMethod.brand.toUpperCase()} ending in {subscriptionDetails.paymentMethod.last4}
                </h3>
                <h3>
                  Expires {subscriptionDetails.paymentMethod.exp_month}/{subscriptionDetails.paymentMethod.exp_year}
                </h3>
              </>
            ) : (
              <h3 style={{ color: "gray" }}>
                Payment method not available. Please check your billing settings on Stripe.
              </h3>
            )}
            {user?.stripeCustomerId && (
              <button
                onClick={async () => {
                  try {
                    const res = await axios.post(
                      "/stripe/create-customer-portal-session",
                      { userId: user._id },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );

                    if (res.data?.url) {
                      window.location.href = res.data.url;
                    } else {
                      toast.error("Failed to open billing portal.");
                    }
                  } catch (err) {
                    console.error("Billing portal error:", err);
                    toast.error("Unable to open billing portal. Please try again later.");
                  }
                }}
                style={{
                  marginTop: "10px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  cursor: "pointer"
                }}
              >
                Manage Billing
              </button>
            )}
          </div>

          <div className="horizontal_line"></div>

          <div className="manage-subscription">
            <h1 style={{ fontSize: "24px", fontWeight: "700", marginTop: "10px" }}>
              Cancel Subscription:
            </h1>
            <div style={{ marginTop: "20px" }}>
              <div className="CancelMessage" style={{ marginBottom: "10px" }}>
                Cancellation takes effect at the end of your current billing cycle. You'll continue to have full access until then.
              </div>
              <button
                type="button"
                className="action-button cancel"
                onClick={() => setShowCancelModal(true)}
                disabled={loading}
              >
                {loading ? "Cancelling..." : "Cancel Subscription"}
              </button>
              {showCancelModal && (
                <ConfirmModal
                  message="Are you sure you want to cancel your subscription?"
                  onConfirm={handleCancelSubscription}
                  onCancel={() => setShowCancelModal(false)}
                />
              )}
            </div>
          </div>
        </>
      )}

      {!isSubscribed && (
        <div className="manage-subscription" style={{ marginTop: "20px", marginBottom: "20px" }}>
          <h2 className="heading-secondary">
            Previous subscription:&nbsp;
            {subscriptionDetails?.planInterval
              ? getPlanLabel(subscriptionDetails.planInterval)
              : "—"}
          </h2>
          <h2 className="heading-secondary">
            Subscription ended: {formatDate(subscriptionDetails?.endDate)}
          </h2>
        </div>
      )}

      {subscriptionDetails?.planInterval &&
       subscriptionDetails?.cancelAtPeriodEnd &&
       subscriptionDetails?.status === "active" && (
        <div className="resume-subscription-container" style={{ marginTop: "20px" }}>
          <p style={{ fontSize: "16px", marginBottom: "10px" }}>
            Your subscription will cancel on <strong>{formatDate(subscriptionDetails.endDate)}</strong>.
          </p>
          <button
            onClick={handleResume}
            className="resume-btn"
            style={{
              backgroundColor: "#B1E5F2",
              color: "black",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              padding: "10px 20px",
              fontSize: "16px",
            }}
          >
            Resume Subscription
          </button>
        </div>
      )}

    </>
  );
};

export default ManageSubscription;
